import { Alert, Button, Column, Icon, Modal, ModalFooter, Row, Text, Tooltip } from '@amzn/meridian';
import linkTokens from '@amzn/meridian-tokens/base/icon/link';
import { cloneDeep, isEqual, uniq } from 'es-toolkit';
import { get, unset } from 'es-toolkit/compat';
import { UsedByDisplay } from 'features/traffic/campaigns/content/shared-variables/ActiveShareableField/UsedByDisplay';
import { useCampaignConfig } from 'features/traffic/campaigns/hooks/useCampaignConfig';
import { createFlavorLabel } from 'features/traffic/campaigns/TPCampaignOverview/overview-items/TPCampaignOverviewFlavorSelector/flavor-cloning-utils';
import type { TrafficCampaignFlavor } from 'generated/graphql';
import { useModalState } from 'hooks/useModalState/useModalState';
import pluralize from 'pluralize';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import type { FieldTemplateProps } from 'types/JourneysDeclarativeUI';
import type {
  DMCCFieldSharingConfig,
  DMCCOverrideMap,
  EffectiveSharingMode,
  SharingConfig,
  useVariableSharingPropagation,
} from '../../variable-sharing';
import {
  evaluateEffectiveSharingMode,
  extractSharingConfig,
  getTargetActiveFlavorIndexes,
  SHARING_MODE,
  SHARING_MODES,
} from '../../variable-sharing';
import { getSharingModeSelections, MARKETPLACE_OPTIONS, PLATFORM_OPTIONS, type SharingCardId } from './constants';
import { SharingActionButtons } from './SharingActionButtons';
import { SharingOptionGroup } from './SharingOptionGroup';

const useModalControls = ({ sharingConfig }: { sharingConfig?: SharingConfig }) => {
  const { open: isOpen, close: closeModal, toggle: toggleModal } = useModalState();
  const isLocked =
    !sharingConfig ||
    !sharingConfig.defaultSharingMode ||
    sharingConfig.forcedSharingMode === SHARING_MODE.ALL ||
    sharingConfig.forcedSharingMode === sharingConfig.defaultSharingMode;

  return {
    closeModal,
    isLocked,
    isOpen,
    toggleModal,
  };
};

/**
 * Manages the sharing state calculations for the modal, including effective sharing mode, available options, and target flavor labels.
 */
const useSharingState = ({
  currentFlavorIndex,
  dmccOverrideMap,
  flavors,
  id,
  sharingConfig,
}: {
  currentFlavorIndex: number;
  dmccOverrideMap?: DMCCOverrideMap;
  flavors: TrafficCampaignFlavor[];
  id: string;
  sharingConfig?: SharingConfig;
}) => {
  const fieldPathArray = useMemo(
    () => id.split('_').slice(1),
    [
      id,
    ],
  );

  const effectiveSharingMode = useMemo(
    () =>
      evaluateEffectiveSharingMode({
        dmccOverrideMap,
        fieldPathArray,
        sharingConfig,
      }),
    [
      dmccOverrideMap,
      fieldPathArray,
      sharingConfig,
    ],
  );

  const defaultSelections = sharingConfig?.defaultSharingMode ? getSharingModeSelections(sharingConfig.defaultSharingMode) : undefined;
  const forcedSelections =
    sharingConfig?.forcedSharingMode && sharingConfig.forcedSharingMode !== SHARING_MODE.STRUCTURE
      ? getSharingModeSelections(sharingConfig.forcedSharingMode)
      : undefined;

  const otherEnabledCardIds: SharingCardId[] = useMemo(() => {
    const modeCardIds = sharingConfig?.otherSharingModes?.flatMap((mode) => Object.values(getSharingModeSelections(mode))) ?? [];

    // Enable no-sharing cards when no forced sharing mode is present
    if (!sharingConfig?.forcedSharingMode) modeCardIds.push('platform-no-sharing', 'marketplace-no-sharing');
    return uniq(modeCardIds);
  }, [
    sharingConfig,
  ]);

  const flavorLabels = useMemo(() => {
    if (!flavors || currentFlavorIndex === undefined || !effectiveSharingMode) return [];

    return getTargetActiveFlavorIndexes({
      allFlavors: flavors,
      currentFlavor: flavors[currentFlavorIndex],
      currentFlavorIndex,
      sharingMode: effectiveSharingMode,
    }).map((index) => createFlavorLabel(flavors[index]));
  }, [
    flavors,
    currentFlavorIndex,
    effectiveSharingMode,
  ]);

  const isOutOfSync = useMemo(
    () =>
      effectiveSharingMode &&
      validateFieldSync({
        currentFlavorIndex,
        effectiveSharingMode,
        fieldPathArray,
        flavors,
      }),
    [
      currentFlavorIndex,
      effectiveSharingMode,
      fieldPathArray,
      flavors,
    ],
  );

  return {
    defaultSelections,
    effectiveSharingMode,
    fieldPathArray,
    flavorLabels,
    forcedSelections,
    isOutOfSync,
    otherEnabledCardIds,
  };
};

/**
 * Manages modal user interactions, selection state, and applies sharing configuration changes.
 * Handles the complex logic of converting UI selections to sharing modes and triggering propagation.
 */
const useModalHandlers = ({
  applyVariableSharingPropagation,
  closeModal,
  dmccConfig,
  fieldPathArray,
  newFormData,
  sharingConfig,
}: {
  closeModal: () => void;
  dmccConfig: DMCCFieldSharingConfig;
  fieldPathArray: PropertyKey[];
  newFormData?: unknown;
  sharingConfig?: SharingConfig;
} & ReturnType<typeof useVariableSharingPropagation>) => {
  const [selectedPlatformCardId, setSelectedPlatformCardId] = useState<string | null>(null);
  const [selectedMarketplaceCardId, setSelectedMarketplaceCardId] = useState<string | null>(null);

  const handleModalClose = useCallback(() => {
    closeModal();
    setSelectedPlatformCardId(null);
    setSelectedMarketplaceCardId(null);
  }, [
    closeModal,
  ]);

  const handleApply = useCallback(() => {
    if (!selectedPlatformCardId || !selectedMarketplaceCardId) return;

    const selectedSharingMode = SHARING_MODES.filter((mode) => mode !== SHARING_MODE.STRUCTURE).find((mode) => {
      const selections = getSharingModeSelections(mode);
      return selections?.marketplaceCardId === selectedMarketplaceCardId && selections?.platformCardId === selectedPlatformCardId;
    });

    if (!selectedSharingMode) return;

    // Remove override if user selected default mode, otherwise set custom override
    const newOverrideMap =
      selectedSharingMode === sharingConfig?.defaultSharingMode
        ? dmccConfig.removeFieldOverride(fieldPathArray)
        : dmccConfig.setFieldOverride(fieldPathArray, selectedSharingMode);

    if (applyVariableSharingPropagation && newFormData) {
      const previousFormData = cloneDeep(newFormData);
      unset(previousFormData, fieldPathArray);

      applyVariableSharingPropagation({
        dmccOverrideMap: newOverrideMap,
        newFormData,
        previousFormData,
      });
    }

    handleModalClose();
  }, [
    selectedPlatformCardId,
    selectedMarketplaceCardId,
    sharingConfig?.defaultSharingMode,
    dmccConfig,
    fieldPathArray,
    applyVariableSharingPropagation,
    newFormData,
    handleModalClose,
  ]);

  return {
    handleApply,
    handleModalClose,
    selectedMarketplaceCardId,
    selectedPlatformCardId,
    setSelectedMarketplaceCardId,
    setSelectedPlatformCardId,
  };
};

/**
 * Validates if field values are synchronized across target flavors according to the effective sharing mode.
 *
 * Compares the field value from the current flavor against all target flavors that should be sharing
 * the same value based on the sharing mode configuration. Uses deep equality comparison to detect
 * value mismatches that indicate the field is out of sync.
 *
 * @returns true if values are out of sync, false if all target flavors have matching values
 */
function validateFieldSync({
  currentFlavorIndex,
  effectiveSharingMode,
  fieldPathArray,
  flavors,
}: {
  currentFlavorIndex: number;
  effectiveSharingMode: EffectiveSharingMode;
  fieldPathArray: PropertyKey[];
  flavors: TrafficCampaignFlavor[];
}): boolean {
  const currentFlavor = flavors[currentFlavorIndex];
  const currentFieldValue = get(currentFlavor.content, fieldPathArray);

  // Get target flavor indexes that should have matching values
  const targetFlavorIndexes = getTargetActiveFlavorIndexes({
    allFlavors: flavors,
    currentFlavor,
    currentFlavorIndex,
    sharingMode: effectiveSharingMode,
  });

  // If no target flavors, then there's nothing to be out of sync with
  if (targetFlavorIndexes.length === 0) return false;

  // Check if any target flavor has a different value
  return targetFlavorIndexes.some((targetIndex) => {
    const flavorValue = get(flavors[targetIndex].content, fieldPathArray);
    return !isEqual(currentFieldValue, flavorValue);
  });
}

/**
 * Modal for configuring how DMCC field values are shared across campaign flavor variations.
 *
 * Provides platform-level and marketplace/language-level sharing options with user override support.
 * The sharing hierarchy prioritizes forced modes, then user overrides, then defaults.
 *
 * Only displays when multiple sharing options are available.
 */
export const VariableSharingModal = ({
  applyVariableSharingPropagation,
  currentFlavorIndex,
  dmccConfig,
  flavors,
  id,
  label,
  uiSchema,
}: FieldTemplateProps & {
  currentFlavorIndex: number;
  dmccConfig: DMCCFieldSharingConfig;
  flavors: TrafficCampaignFlavor[];
} & ReturnType<typeof useVariableSharingPropagation>) => {
  const campaignConfig = useCampaignConfig();
  const disablePlatformSharing = campaignConfig.sharingOptions?.disablePlatformSharing ?? false;
  const sharingConfig = extractSharingConfig(uiSchema);

  const hasMultipleFlavors = flavors.filter((flavor) => flavor.active).length > 1;
  const isVisible = hasMultipleFlavors && sharingConfig && uiSchema?.[`ui:widget`] !== 'hidden';

  const { closeModal, isLocked, isOpen, toggleModal } = useModalControls({
    sharingConfig,
  });

  const { defaultSelections, effectiveSharingMode, fieldPathArray, flavorLabels, forcedSelections, isOutOfSync, otherEnabledCardIds } =
    useSharingState({
      currentFlavorIndex,
      dmccOverrideMap: dmccConfig.dmccOverrideMap,
      flavors,
      id,
      sharingConfig,
    });

  const {
    input: { value: newFormData },
  } = useField(`flavors[${currentFlavorIndex}].content`, {
    subscription: {
      value: true,
    },
  });

  const {
    handleApply,
    handleModalClose,
    selectedMarketplaceCardId,
    selectedPlatformCardId,
    setSelectedMarketplaceCardId,
    setSelectedPlatformCardId,
  } = useModalHandlers({
    applyVariableSharingPropagation,
    closeModal,
    dmccConfig,
    fieldPathArray,
    newFormData,
    sharingConfig,
  });

  useEffect(() => {
    if (isOpen && effectiveSharingMode) {
      const effectiveSelections = getSharingModeSelections(effectiveSharingMode);
      if (effectiveSelections) {
        setSelectedPlatformCardId(effectiveSelections.platformCardId);
        setSelectedMarketplaceCardId(effectiveSelections.marketplaceCardId);
      }
    }
  }, [
    effectiveSharingMode,
    isOpen,
    setSelectedPlatformCardId,
    setSelectedMarketplaceCardId,
  ]);

  const inputLabel = label?.trim() || '';

  return (
    isVisible && (
      <>
        <Row alignmentVertical="center" spacing="300">
          <Icon tokens={linkTokens} />
          <Row spacing="200">
            <Text type="b200">Shared with </Text>
            <UsedByDisplay
              flavorLabels={flavorLabels}
              usedByText={`${flavorLabels.length} other ${pluralize('variation', flavorLabels.length)}`}
            />
          </Row>
          {isOutOfSync && (
            <Tooltip
              delay={500}
              position="bottom"
              title="This value is currently out of sync with other variations. Update this value or change the sharing to sync."
            >
              <Row>
                <Alert size="small" type="warning">
                  Out of sync
                </Alert>
              </Row>
            </Tooltip>
          )}
          <SharingActionButtons isLocked={isLocked} onOpenModal={toggleModal} />
        </Row>
        <Modal bodySpacingInset="500" data-cy="change-shared-value-modal" open={isOpen} title={`Automatic sharing for '${inputLabel}'`}>
          <Column spacing="500">
            <Column spacing="400">
              <Text>
                Use the options here to configure how this property's values should be automatically shared across variations in this
                campaign. Some sharing methods may not be available.
              </Text>
              {!disablePlatformSharing && (
                <>
                  <Text>
                    1. Should the value used for '{inputLabel}' be the <b>same across all platforms</b>?
                  </Text>
                  <SharingOptionGroup
                    checkedCardId={selectedPlatformCardId}
                    defaultCardId={defaultSelections?.platformCardId}
                    forcedCardId={forcedSelections?.platformCardId}
                    onSelect={setSelectedPlatformCardId}
                    options={PLATFORM_OPTIONS}
                    otherEnabledCardIds={otherEnabledCardIds}
                  />
                </>
              )}
            </Column>
            <Column spacing="400">
              <Text>
                {!disablePlatformSharing && '2. '}Should the value used for '{inputLabel}' be the{' '}
                <b>same across languages and/or marketplaces</b>?
              </Text>
              <SharingOptionGroup
                checkedCardId={selectedMarketplaceCardId}
                defaultCardId={defaultSelections?.marketplaceCardId}
                forcedCardId={forcedSelections?.marketplaceCardId}
                onSelect={setSelectedMarketplaceCardId}
                options={MARKETPLACE_OPTIONS}
                otherEnabledCardIds={otherEnabledCardIds}
              />
            </Column>
            <ModalFooter>
              <Row alignmentHorizontal="end">
                <Button data-cy="cancel-shared-value" onClick={handleModalClose} type="secondary">
                  Cancel
                </Button>
                <Button data-cy="apply-shared-value" onClick={handleApply} type="primary">
                  Apply
                </Button>
              </Row>
            </ModalFooter>
          </Column>
        </Modal>
      </>
    )
  );
};
